/**
 * Sanitizes a URL by removing query parameters and hash fragments.
 * Helps prevent leaking sensitive data (API keys, tokens) in metrics and logs.
 * @param {string} url - URL to sanitize
 * @returns {string} Sanitized URL without query params or hash
 */
export const sanitizeUrl = (url) => {
	if (!url || typeof url !== 'string') {
		return url;
	}

	try {
		const urlObj = new URL(url);
		urlObj.search = '';
		urlObj.hash = '';
		return urlObj.toString();
	} catch {
		return url.split('?')[0].split('#')[0];
	}
};

const SENSITIVE_PATTERNS = [
	/Bearer\s+[^\s]+/gi,
	/token[=:]\s*[^\s&]+/gi,
	/api[_-]?key[=:]\s*[^\s&]+/gi,
	/password[=:]\s*[^\s&]+/gi,
	/secret[=:]\s*[^\s&]+/gi,
	/client[_-]?secret[=:]\s*[^\s&]+/gi,
	/Authorization:\s*[^\r\n]+/gi,
	/eyJ[A-Za-z0-9_-]*\.eyJ[A-Za-z0-9_-]*\.[A-Za-z0-9_-]*/g,
];

/**
 * Sanitizes an error message by redacting sensitive patterns.
 * Removes Bearer tokens, API keys, passwords, secrets, and other credentials.
 * @param {string} message - Error message to sanitize
 * @returns {string} Sanitized message with sensitive data replaced with [REDACTED]
 */
export const sanitizeErrorMessage = (message) => {
	if (!message || typeof message !== 'string') {
		return message;
	}

	let sanitized = message;
	SENSITIVE_PATTERNS.forEach((pattern) => {
		sanitized = sanitized.replace(pattern, '[REDACTED]');
	});

	return sanitized;
};
